'use strict';

//dependencies included for running in node
if (typeof exports !== 'undefined') {
  if (typeof module !== 'undefined' && module.exports) {
    var Annuity = require('./model/annuity');
    var Expectancy = require('./model/expectancy');
    var EmployerMatchTable = require('./tables/employerMatchTable');
    var InvestmentTable = require('./tables/investmentTable');
    var DrawdownTable = require('./tables/drawdownTable');
    var ClassUtil = require('./lib/classUtil');
    var AdvDate = require('js-utils/src/util/advDate');
  }
}

var Parameters = function(data) {
  // recursively merge JSON object into parameters Object 
  // so properties can be accessed directly from Parameters
  ClassUtil.merge(this, data);

  // parse date from string value. If not specified, use today's date as calculation date
  this.model.calculationDate = this.model && this.model.calculationDate === null ? AdvDate.today() : new AdvDate(this.model.calculationDate);
  
  // remove potential floating point rounding issues from contribution rate which can break employer match look-up
  // NOTE: this limits max precision of contribution rate to 0.5%
  this.person.contributionRate = Number(this.person.contributionRate.toFixed(4));

  // initialise tables
  if (this.employerMatch) this.employerMatchTable = new EmployerMatchTable(this);
  if (this.preRetirementInvestment) this.preRetirementInvestmentTable = new InvestmentTable(this.preRetirementInvestment);
  if (this.postRetirementInvestment) this.postRetirementInvestmentTable = new InvestmentTable(this.postRetirementInvestment);
  if (this.drawdownFrequency) this.drawdownTable = new DrawdownTable(this.drawdownFrequency);
  if (this.annuity) this.annuityModel = new Annuity(this.annuity);
  if (this.lifeExpectancy) this.expectancyModel = new Expectancy(this.lifeExpectancy);
};

Parameters.ONE_DAY = 1/AdvDate.DAYS_IN_YEAR;

Parameters.prototype.dateOfBirth = function() {
  // if date of birth specified as a date, then use the date
  if (this.person.dateOfBirth !== null) {
    return new AdvDate(this.person.dateOfBirth);
  }

  // if no date specified, use age as an approximation
  var today = AdvDate.today();
  return new AdvDate(today.getFullYear() - this.person.age, today.getMonth(), today.getDate());
};

Parameters.prototype.calculationAge = function() {
  // age is an optional parameter. This is the safe way to get age
  return Math.floor(this.model.calculationDate.minus(this.dateOfBirth()));
};

Parameters.prototype.ageNow = function() {
  // age is an optional parameter. This is the safe way to get age
  return Math.floor(AdvDate.today().minus(this.dateOfBirth()));
};

Parameters.prototype.yearOfBirth = function() {
  return this.dateOfBirth().getFullYear();
};

Parameters.prototype.retirementAge = function() {
  var retirementAge = this.retirementDate().minus(this.dateOfBirth());

  // if there is a rounding issue, and we are within a day of the next full year, return next full year
  if(Math.ceil(retirementAge) - retirementAge < Parameters.ONE_DAY) {
    return Math.ceil(retirementAge);
  }
  return Math.floor(retirementAge);
};

Parameters.prototype.retirementDate = function() {
  // if retirement date specified as a date, then use the date
  if (this.person.retirementDate !== null) {
    return new AdvDate(this.person.retirementDate);
  }

  // if not date specified, use age + retirement age as an approximation
  var dateOfBirth = this.dateOfBirth();
  return new AdvDate(dateOfBirth.getFullYear() + this.person.retirementAge, dateOfBirth.getMonth(), dateOfBirth.getDate());
};

Parameters.prototype.getEeContributionRate = function() {
  var eeContributionRate = this.person.contributionRate;
  
  if (!this.model.applyScale) {
    return eeContributionRate;
  }
  
  if (this.model.eesErsIncludeScale === false) {
    return eeContributionRate + this.employerMatchTable.getEmployeeMatch();
  }
  
  return eeContributionRate;
};

Parameters.prototype.getErContributionRate = function(employeeRate, increment) {
  var erContributionRate = this.model.erContributionRate;

  if (!this.model.applyScale) {
    return erContributionRate;
  }
  
  if (this.model.eesErsIncludeScale === false) {
    return erContributionRate + this.getEmployerMatch(this.employerMatchTable.getEmployeeMatch(increment), increment);
  }

  return this.getEmployerMatch(employeeRate, increment);
};

Parameters.prototype.getEmployerMatch = function(employeeRate, increment) {
  return this.employerMatchTable.getMatch(employeeRate, increment);
};

Parameters.prototype.monthsToRetirement = function() {
  return AdvDate.monthsBetween(this.model.calculationDate, this.retirementDate());
};

Parameters.prototype.maximumProjectionDate = function() {
  var dateOfBirth = this.dateOfBirth();
  return new AdvDate(dateOfBirth.getFullYear() + this.model.maximumProjectionAge, dateOfBirth.getMonth(), dateOfBirth.getDate());
};

Parameters.prototype.monthsToMaximumProjectionDate = function() {
  return AdvDate.monthsBetween(this.model.calculationDate, this.maximumProjectionDate());
};

Parameters.prototype.firstAutoEnrolmentDate = function() {
  return new AdvDate(this.model.firstAutoEnrolmentDate); 
};

Parameters.prototype.secondAutoEnrolmentDate = function() {
  return new AdvDate(this.model.secondAutoEnrolmentDate);
};

Parameters.prototype.monthsToFirstAutoEnrolmentDate = function() {
  return AdvDate.monthsBetween(this.model.calculationDate, this.firstAutoEnrolmentDate());
};

Parameters.prototype.monthsToSecondAutoEnrolmentDate = function() {
  return AdvDate.monthsBetween(this.model.calculationDate, this.secondAutoEnrolmentDate());
};

Parameters.prototype.annuityRate = function() {
  return this.annuityModel.rate(this.person.gender, this.yearOfBirth(), this.retirementAge(), this.person.annuityOptions);
};

Parameters.prototype.annuityExpense = function() {
  return this.annuityModel.expense();
};

Parameters.prototype.minimumRetirementAge = function() {
  return this.annuityModel.minimumAge();
};

Parameters.prototype.maximumRetirementAge = function() {
  return this.annuityModel.maximumAge();
};

Parameters.prototype.lifeExpectancy = function() {
  return this.expectancyModel.expectancy(this.person.gender, this.person.age);
};

//TODO: maximumAge(Now) based on available annuity rates


//export for node
if (typeof exports !== 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
        exports = module.exports = Parameters;
    }
    exports.Parameters = Parameters;
}